import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import { Location, Customer, CardType } from '../../../models/index';
import {
  FormGroup,
  FormControl,
  Validators,
  FormBuilder,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  LocationService,
  MerchantService,
  AuthService,
} from 'src/app/services';
import { Constants } from 'src/app/app.constants';
import { MatDialog } from '@angular/material/dialog';
import { TwoOptionAlertComponent } from 'src/app/shared/components/two-option-alert/two-option-alert.component';
import { NoopScrollStrategy } from '@angular/cdk/overlay';

@Component({
  selector: 'give-stamps-dialog',
  templateUrl: './give-stamps-dialog.component.html',
  styleUrls: ['./give-stamps-dialog.component.css'],
})
export class GiveStampsDialogComponent implements OnInit {
  form!: FormGroup;
  customerFormControl!: FormControl;
  cardFormControl!: FormControl;
  stampsFormControl!: FormControl;
  pointsFormControl!: FormControl;
  locationFormControl!: FormControl;

  loading = false;
  locationsLoading = false;
  customersLoading = false;
  cardsLoading = false;
  error = false;

  locations: Location[] = [];
  customers: Customer[] = [];
  customerCards: any[] = [];
  stampOptions: any[] = [];

  isStampCard = false;
  selectedCard: any = null;
  isFromCustomerDetail = false;
  hasPreselectedCustomer = false;
  notify = false;

  constructor(
    public matDialogRef: MatDialogRef<GiveStampsDialogComponent>,
    public fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private locationService: LocationService,
    private merchantService: MerchantService,
    private constants: Constants,
    private dialog: MatDialog,
    private authService: AuthService,
  ) {
    // Check if we have pre-selected customer and card (from customer detail page card click)
    if (this.data?.customer && this.data?.userCard) {
      this.isFromCustomerDetail = true;
    }
    // Check if we have pre-selected customer but need to select card (from customer detail page button)
    else if (this.data?.customer && !this.data?.userCard) {
      this.isFromCustomerDetail = false; // Show card selection but pre-populate customer
      this.hasPreselectedCustomer = true;
    }
  }

  ngOnInit(): void {
    // Set up form controls with conditional validation
    this.customerFormControl = new FormControl(
      '',
      this.isFromCustomerDetail ? [] : [Validators.required],
    );
    this.cardFormControl = new FormControl(
      '',
      this.isFromCustomerDetail ? [] : [Validators.required],
    );
    this.stampsFormControl = new FormControl('', []);
    this.pointsFormControl = new FormControl('', []);
    this.locationFormControl = new FormControl('', []);

    this.form = this.fb.group({
      customer: this.customerFormControl,
      card: this.cardFormControl,
      stamps: this.stampsFormControl,
      points: this.pointsFormControl,
      location: this.locationFormControl,
    });

    this.getLocations();

    if (this.isFromCustomerDetail) {
      // Pre-populate with provided customer and card
      this.customerFormControl.setValue(this.data.customer);
      this.selectedCard = this.data.userCard;
      this.cardFormControl.setValue(this.data.userCard);
      this.setupCardType(this.data.userCard);
    } else if (this.hasPreselectedCustomer) {
      // Pre-populate customer but allow card selection
      this.customerFormControl.setValue(this.data.customer);
      this.getCustomerCards(this.data.customer._id!);
      this.getCustomers(); // Still load customers for consistency
    } else {
      // Load customers for selection
      this.getCustomers();
    }
  }

  getLocations(): void {
    this.locationsLoading = true;

    this.locationService
      .getLocations(1000, 0)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.locations = res.docs.filter(
              (el: Location) => el.active === true && el.approved === true,
            );

            this.locations = this.locations.map((el: Location) => {
              return {
                ...el,
                formattedAddress: this.constants.constructAddress(el.address!),
              };
            });

            if (this.locations?.length < 2) {
              this.locationFormControl.clearValidators();
              this.locationFormControl.updateValueAndValidity();
            }
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => {
        this.locationsLoading = false;
      });
  }

  getCustomers(): void {
    this.customersLoading = true;
    this.merchantService
      .getCustomers('_id', 'desc', 2000, 0, '', {})
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.customers = res.docs;

            this.customers.forEach((el) => {
              if (typeof el.user !== 'string') {
                el.name = el.user?.name ?? el?.name;
                el.email = el.user?.email ?? el?.email;
              }
            });

            this.customers.sort((a, b) => a.name!.localeCompare(b.name!));
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.customersLoading = false));
  }

  onCustomerSelected(customer: Customer): void {
    this.cardFormControl.setValue('');
    this.customerCards = [];
    this.selectedCard = null;
    this.isStampCard = false;

    if (customer) {
      this.getCustomerCards(customer._id!);
    }
  }

  onCardSelected(card: any): void {
    this.selectedCard = card;
    if (card) {
      this.setupCardType(card);
    }
  }

  setupCardType(card: any): void {
    const cardData = card.card[0] ?? card.card;
    this.isStampCard = cardData?.cardType === CardType.stamp;

    if (this.isStampCard) {
      // Generate stamp options based on card's numberOfStamps
      this.stampOptions = [];
      const maxStamps = cardData?.numberOfStamps || 10;
      for (let i = 1; i <= maxStamps; i++) {
        this.stampOptions.push({ number: i });
      }
      this.stampsFormControl.setValidators([Validators.required]);
      this.pointsFormControl.clearValidators();
    } else {
      this.pointsFormControl.setValidators([Validators.required]);
      this.stampsFormControl.clearValidators();
    }

    this.stampsFormControl.updateValueAndValidity();
    this.pointsFormControl.updateValueAndValidity();
  }

  getCustomerCards(customerId: string): void {
    this.cardsLoading = true;
    this.merchantService
      .getCustomerCards(1000, 0, customerId)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.customerCards = res.docs.map((userCard: any) => {
              const cardData = userCard.card[0] ?? userCard.card;
              return {
                ...userCard,
                cardTitle: cardData?.title || 'Untitled Card',
                info: cardData?.description || '',
              };
            });
          }
        },
        error: (res: HttpErrorResponse) => {
          this.error = true;
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.cardsLoading = false));
  }

  canSubmit(): boolean {
    // For customer detail page, customer and card are pre-selected
    if (this.isFromCustomerDetail) {
      if (this.isStampCard) {
        return !!this.stampsFormControl.value;
      } else {
        return (
          !!this.pointsFormControl.value && this.pointsFormControl.value > 0
        );
      }
    }

    // For preselected customer or customers page, need customer and card selection
    if (!this.customerFormControl.value || !this.cardFormControl.value) {
      return false;
    }

    if (this.isStampCard) {
      return !!this.stampsFormControl.value;
    } else {
      return !!this.pointsFormControl.value && this.pointsFormControl.value > 0;
    }
  }

  primaryTapped() {
    if (!this.canSubmit()) {
      this.form.markAllAsTouched();
      return;
    }

    const customer = this.customerFormControl.value;
    const userCard = this.cardFormControl.value;
    const quantity = this.isStampCard
      ? this.stampsFormControl.value?.number
      : this.pointsFormControl.value;

    let location: string | undefined;
    if (this.locations.length > 1 && this.locationFormControl.value) {
      location = this.locationFormControl.value._id;
    }

    if (!Number.isInteger(quantity) || quantity <= 0) {
      this.constants.snack('Number must be a positive whole number');
      return;
    }

    // Validate and show confirmation dialog
    if (this.isStampCard) {
      this.processStampGiving(customer, userCard, quantity, location);
    } else {
      this.processPointsGiving(customer, userCard, quantity, location);
    }
  }

  processStampGiving(
    customer: any,
    userCard: any,
    quantity: number,
    location?: string,
  ) {
    const rewards = this.rewardsToGive(quantity, userCard);

    const dialogRef = this.dialog.open(TwoOptionAlertComponent, {
      data: {
        title: 'Confirm',
        body: `Do you want to give this customer ${quantity} ${
          quantity > 1 ? 'stamps' : 'stamp'
        }? ${
          rewards > 0
            ? ' <br><br>They will also receive ' +
              rewards +
              (rewards === 1 ? ' reward' : ' rewards')
            : ''
        }`,
        buttonOne: 'Cancel',
        buttonTwo: 'Yes',
      },
      scrollStrategy: new NoopScrollStrategy(),
      autoFocus: false,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((option: number) => {
      if (option == 1) {
        this.executeGiveStamp(customer, userCard, quantity, location);
      }
    });
  }

  processPointsGiving(
    customer: any,
    userCard: any,
    quantity: number,
    location?: string,
  ) {
    const cardData = userCard?.card[0] ?? userCard?.card;
    const isFixedPointsCard = cardData?.cardType === CardType.point;
    const isOpenEndedPointsCard =
      cardData?.cardType === CardType.openEndedPoint;

    const rewards = this.rewardsToGiveFromPoints(quantity, userCard);
    let totalPointsAvailable = 0;

    if (isFixedPointsCard) {
      totalPointsAvailable =
        cardData?.points?.totalPoints - userCard?.numberOfPoints;
    }

    if (isOpenEndedPointsCard) {
      if (cardData?.points?.maxPoints > 0) {
        totalPointsAvailable =
          cardData?.points?.maxPoints - userCard?.numberOfPoints;
      } else {
        totalPointsAvailable = 999999;
      }
    }

    if (quantity > 0) {
      if (quantity > totalPointsAvailable && totalPointsAvailable !== 0) {
        this.constants.snack(
          `You cannot give more than ${totalPointsAvailable} points`,
        );
        return;
      }

      if (quantity > totalPointsAvailable && isOpenEndedPointsCard) {
        this.constants.snack(
          `Customer has reached the maximum number of points for this card`,
        );
        return;
      }

      const dialogRef = this.dialog.open(TwoOptionAlertComponent, {
        data: {
          title: 'Confirm',
          body: `Do you want to give this customer ${quantity} ${
            quantity > 1 ? 'points' : 'point'
          }? ${
            rewards > 0
              ? ' <br><br>They will also receive ' +
                rewards +
                (rewards === 1 ? ' reward' : ' rewards')
              : ''
          }`,
          buttonOne: 'Cancel',
          buttonTwo: 'Yes',
        },
        scrollStrategy: new NoopScrollStrategy(),
        autoFocus: false,
        disableClose: true,
      });

      dialogRef.afterClosed().subscribe((option: number) => {
        if (option == 1) {
          this.executeGiveStamp(customer, userCard, quantity, location);
        }
      });
    }
  }

  executeGiveStamp(
    customer: any,
    userCard: any,
    quantity: number,
    location?: string,
  ) {
    this.loading = true;

    const userId =
      typeof customer.user === 'string' ? customer.user : customer.user?._id;

    this.merchantService
      .giveStamp(userId, userCard._id, quantity, location, this.notify)
      .subscribe({
        next: (res: any) => {
          if (res) {
            this.constants.snack(
              this.isStampCard
                ? 'Stamp(s) awarded to customer'
                : 'Point(s) awarded to customer',
            );
            this.matDialogRef.close({ success: true });
          }
        },
        error: (res: HttpErrorResponse) => {
          this.constants.snack(res.error.message);
        },
      })
      .add(() => (this.loading = false));
  }

  rewardsToGive(stamps: number, userCard: any): number {
    const totalStamps = (userCard.numberOfStamps ?? 0) + stamps;
    let rewardsCount = 0;
    const rewards = userCard?.card[0]?.rewards ?? userCard?.card?.rewards ?? [];

    if (rewards && rewards.length > 0) {
      rewards.forEach((reward: any) => {
        if (
          userCard.numberOfStamps < reward.stamp &&
          totalStamps >= reward.stamp
        ) {
          rewardsCount++;
        }
      });
    }

    return rewardsCount;
  }

  rewardsToGiveFromPoints(points: number, userCard: any): number {
    const cardData = userCard?.card[0] ?? userCard?.card;
    const totalPoints = (userCard.numberOfPoints ?? 0) + points;
    let rewardsCount = 0;
    const rewards = cardData?.rewards ?? [];

    if (rewards && rewards.length > 0) {
      rewards.forEach((reward: any) => {
        if (
          reward.points &&
          userCard.numberOfPoints < reward.points &&
          totalPoints >= reward.points
        ) {
          rewardsCount++;
        }
      });
    }

    return rewardsCount;
  }

  getNotificationPreview(): string {
    if (!this.cardFormControl.value || !this.notify) {
      return '';
    }

    const merchantName = this.authService.merchantObj?.name || 'Merchant';
    let quantity = 0;
    let unit = '';

    if (this.isStampCard && this.stampsFormControl.value) {
      quantity = this.stampsFormControl.value.number;
      unit = quantity === 1 ? 'stamp' : 'stamps';
    } else if (!this.isStampCard && this.pointsFormControl.value) {
      quantity = this.pointsFormControl.value;
      unit = quantity === 1 ? 'point' : 'points';
    }

    if (quantity > 0) {
      return `You have been given ${quantity} ${unit} by ${merchantName} 🎉`;
    }

    const placeholderUnit = this.isStampCard ? 'stamps' : 'points';
    return `You have been given X ${placeholderUnit} by ${merchantName} 🎉`;
  }
}
